import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  toast,
} from "@strivelabs/ui-core"
import { Loader2 } from "lucide-react"
import { PasswordInput } from "@/components/common"
import { useCreateApiKeyConnection, useUpdateApiKeyConnection } from "@/hooks/useConnections"
import { logger } from "@/lib/logger"
import type { App } from "@/services/DataService"

interface ApiKeyConnectModalProps {
  app: App | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
  /** When set, the modal updates this existing credential's key instead of creating a new connection. */
  credentialId?: string
}

const formSchema = z.object({
  display_name: z
    .string()
    .trim()
    .min(1, "Connection name is required"),
  api_key: z
    .string()
    .trim()
    .min(1, "API key is required"),
})

type FormData = z.infer<typeof formSchema>

export function ApiKeyConnectModal({ app, open, onOpenChange, onSuccess, credentialId }: ApiKeyConnectModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const createConnection = useCreateApiKeyConnection()
  const updateConnection = useUpdateApiKeyConnection()
  const isUpdate = Boolean(credentialId)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: { display_name: "", api_key: "" },
  })

  useEffect(() => {
    if (open && app) {
      reset({
        display_name: app.display_name,
        api_key: "",
      })
      setSubmitError(null)
    }
  }, [open, app, reset])

  const onSubmit = async (form: FormData) => {
    if (!app) return

    const authName = app.auth[0]?.short_name ?? "api_key"
    const payload = {
      app_name: app.short_name,
      auth_name: authName,
      display_name: form.display_name,
      token_type: "api_key" as const,
      api_key: form.api_key,
    }

    setIsSubmitting(true)
    setSubmitError(null)
    try {
      if (credentialId) {
        await updateConnection.mutateAsync({ credentialId, payload })
      } else {
        await createConnection.mutateAsync({ appName: app.short_name, payload })
      }

      toast({
        title: isUpdate ? "Updated" : "Connected",
        description: isUpdate
          ? `${app.display_name} API key updated.`
          : `${app.display_name} connected successfully.`,
      })
      onOpenChange(false)
      onSuccess?.()
    } catch (error) {
      // Log only the message so the api_key can't leak via an error object
      // that echoes the request body.
      const message = error instanceof Error ? error.message : "Unknown error"
      logger(new Error(message), { context: "ApiKeyConnectModal: connect failed" })
      setSubmitError("We couldn't save your API key. Please check the value and try again.")
      toast({
        title: "Connection Failed",
        description: "We couldn't save your API key. Please check the value and try again.",
        variant: "destructive",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!isSubmitting) onOpenChange(next) }}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{isUpdate ? "Update" : "Connect"} {app?.display_name ?? ""}</DialogTitle>
          <DialogDescription>
            {isUpdate
              ? `Enter a new API key to replace the current one for ${app?.display_name ?? "this service"}. It is stored encrypted and used only to call the service on your behalf.`
              : `Paste your ${app?.display_name ?? "service"} API key. It is stored encrypted and used only to call the service on your behalf.`}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="api-key-display-name">
              Connection name <sup className="text-red-500">*</sup>
            </Label>
            <Input
              id="api-key-display-name"
              placeholder={`e.g., My ${app?.display_name ?? ""} account`}
              disabled={isSubmitting}
              {...register("display_name")}
              className={errors.display_name ? "border-red-500" : ""}
            />
            {errors.display_name && (
              <p className="text-xs text-red-500">{errors.display_name.message}</p>
            )}
          </div>
          <div className="grid gap-2">
            <Label htmlFor="api-key-value">
              API key <sup className="text-red-500">*</sup>
            </Label>
            <PasswordInput
              id="api-key-value"
              placeholder="Paste your API key"
              autoComplete="off"
              spellCheck={false}
              disabled={isSubmitting}
              {...register("api_key")}
              className={errors.api_key ? "border-red-500" : ""}
            />
            {errors.api_key && (
              <p className="text-xs text-red-500">{errors.api_key.message}</p>
            )}
          </div>

          {submitError && (
            <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
              {submitError}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" variant="black" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  {isUpdate ? "Updating..." : "Connecting..."}
                </>
              ) : (
                isUpdate ? "Update" : "Connect"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
